// src/pages/school_exams/template_builder/DraggableItem.js
import React from "react";
import { Rnd } from "react-rnd";

export default function DraggableItem({
  item,
  selected,
  onSelect,
  onUpdate,
  scale = 1,
}) {
  const onDragStop = (e, data) => {
    if (item.locked) return;
    onUpdate(item.id, { x: data.x, y: data.y });
  };

  const onResizeStop = (e, dir, ref, delta, pos) => {
    if (item.locked) return;
    onUpdate(item.id, {
      w: parseInt(ref.style.width, 10),
      h: parseInt(ref.style.height, 10),
      x: pos.x,
      y: pos.y,
    });
  };

  const textAlign =
    item.align === "center"
      ? "center"
      : item.align === "right"
      ? "right"
      : "left";

  const renderContent = () => {
    // TEXT
    if (item.type === "text") {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent:
              textAlign === "center"
                ? "center"
                : textAlign === "right"
                ? "flex-end"
                : "flex-start",
            textAlign,
            fontSize: item.fontSize || 18,
            fontWeight: item.bold ? "700" : "400",
            color: item.color || "#111827",
            padding: 4,
            overflow: "hidden",
            whiteSpace: "pre-line",
          }}
        >
          {item.text}
        </div>
      );
    }

    // PHOTO
    if (item.type === "photo") {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: item.borderRadius || 0,
            border: `${item.borderWidth || 1}px solid #000`,
            overflow: "hidden",
            background: "#e5e7eb",
          }}
        >
          {item.imageUrl ? (
            <img
              src={item.imageUrl}
              alt="Student"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                color: "#4b5563",
              }}
            >
              PHOTO
            </div>
          )}
        </div>
      );
    }

    // LINE
    if (item.type === "line") {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: "100%",
              borderBottom: `${item.thickness || 2}px solid ${
                item.color || "#000"
              }`,
            }}
          />
        </div>
      );
    }

    // TABLE
    if (item.type === "table") {
      const headerHeight = item.headerHeight || 30;
      const rowHeight = item.rowHeight || 24;
      const fontSize = item.fontSize || 11;

      const headerBg = item.headerBgColor || "#00c851";
      const headerTextColor = item.headerTextColor || "#000000";
      const rowBg = item.rowBgColor || "#ffffff";
      const altRowBg = item.altRowBgColor || "#f9fafb";
      const borderColor = item.borderColor || "#000000";
      const borderWidth = item.borderWidth || 0.5;

      return (
        <table
          style={{
            width: "100%",
            height: "100%",
            borderCollapse: "collapse",
            fontSize,
            background: "rgba(255,255,255,0.9)",
          }}
        >
          <thead>
            <tr>
              {item.columns.map((col) => (
                <th
                  key={col.id}
                  style={{
                    border: `${borderWidth}px solid ${borderColor}`,
                    padding: 2,
                    background: headerBg,
                    color: headerTextColor,
                    height: headerHeight,
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {item.rows.map((row, rIndex) => (
              <tr key={row.id}>
                {item.columns.map((col, ci) => (
                  <td
                    key={col.id}
                    style={{
                      border: `${borderWidth}px solid ${borderColor}`,
                      padding: 3,
                      height: rowHeight,
                      background: rIndex % 2 === 0 ? rowBg : altRowBg,
                    }}
                  >
                    {row.cells[ci] || ""}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    // RECT
    if (item.type === "rect") {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            background: item.fill || "rgba(0,0,0,0.05)",
            border: `${item.strokeWidth || 1}px solid ${
              item.stroke || "#000"
            }`,
          }}
        />
      );
    }

    // CIRCLE
    if (item.type === "circle") {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            background: item.fill || "rgba(0,0,0,0.05)",
            border: `${item.strokeWidth || 1}px solid ${
              item.stroke || "#000"
            }`,
          }}
        />
      );
    }

    return null;
  };

  return (
    <Rnd
      size={{ width: item.w, height: item.h }}
      position={{ x: item.x, y: item.y }}
      onDragStop={onDragStop}
      onResizeStop={onResizeStop}
      enableResizing={
        item.locked
          ? false
          : item.type === "line"
          ? { left: true, right: true, top: false, bottom: false }
          : undefined
      }
      disableDragging={!!item.locked}
      bounds="parent"
      scale={scale}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(item.id, e.shiftKey);
      }}
      style={{
        border: selected ? "2px solid #3b82f6" : "1px dashed transparent",
        cursor: item.locked ? "default" : "move",
        background: "transparent",
      }}
    >
      {renderContent()}
    </Rnd>
  );
}
